import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, MapPin, Clock } from 'lucide-react';
import events from '../data/events';
import Footer from '../components/Footer';

const Calendar = () => {
  const [currentView, setCurrentView] = useState('month');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedFilters, setSelectedFilters] = useState({
    holiday: true,
    academic: true,
    earlyDismissal: true,
    testing: true,
    pta: true,
    afterSchool: true
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [language, setLanguage] = useState('en');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);

  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    const firstDay = new Date(year, month, 1).getDay();
    for (let i = 0; i < firstDay; i++) days.push(null);
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  };

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getWeekDays = () => {
    const base = new Date(currentYear, currentMonth, 1);
    const start = getStartOfWeek(base);
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + currentWeekOffset * 7);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return { weekStart, days };
  };

  const getFilteredEvents = () => {
    return events.filter(event => selectedFilters[event.category]);
  };

  const getUpcomingEvents = () => {
    const today = new Date();
    return getFilteredEvents()
      .filter(event => event.date >= today)
      .sort((a, b) => a.date - b.date)
      .slice(0, 5);
  };

  const prevMonth = () => {
    if (currentView === 'week') { setCurrentWeekOffset(currentWeekOffset - 1); return; }
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); } else { setCurrentMonth(currentMonth - 1); }
  };

  const nextMonth = () => {
    if (currentView === 'week') { setCurrentWeekOffset(currentWeekOffset + 1); return; }
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); } else { setCurrentMonth(currentMonth + 1); }
  };

  const categoryColors = {
    holiday: 'bg-red-700',
    academic: 'bg-amber-700',
    earlyDismissal: 'bg-orange-700',
    testing: 'bg-purple-700',
    pta: 'bg-yellow-700',
    afterSchool: 'bg-green-700'
  };

  const getCategoryName = (category) => {
    switch(category) {
      case 'holiday': return 'Holiday';
      case 'academic': return 'Academic';
      case 'earlyDismissal': return 'Early Dismissal';
      case 'testing': return 'Testing';
      case 'pta': return 'PTA';
      case 'afterSchool': return 'After-School';
      default: return category;
    }
  };

  const getCategoryNameES = (category) => {
    switch(category) {
      case 'holiday': return 'Feriado';
      case 'academic': return 'Académico';
      case 'earlyDismissal': return 'Salida Temprana';
      case 'testing': return 'Exámenes';
      case 'pta': return 'PTA';
      case 'afterSchool': return 'Programas Después de Clases';
      default: return category;
    }
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(date).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  const getEventTitle = (event) => (language === 'es' && event.titleES ? event.titleES : event.title);
  const getEventDescription = (event) => (language === 'es' && event.descriptionES ? event.descriptionES : (event.description || ''));

  const toggleFilter = (filter) => setSelectedFilters({ ...selectedFilters, [filter]: !selectedFilters[filter] });

  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === day.getDate() &&
             eventDate.getMonth() === day.getMonth() &&
             eventDate.getFullYear() === day.getFullYear() &&
             (selectedFilters[event.category] || false);
    });
  };

  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

  const days = getDaysInMonth(currentMonth, currentYear);
  const upcomingEvents = getUpcomingEvents();
  const closeModal = () => setSelectedEvent(null);

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-16 sm:mb-20">
          <span className="text-[#7d5800] font-semibold text-sm uppercase tracking-[0.15em]">Important Dates</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#123f1e] mt-4 leading-[1.15] tracking-tight">
            School Calendar
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mt-6 leading-relaxed font-light max-w-3xl mx-auto">
            Stay informed about school events, holidays, and important dates throughout the academic year.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar */}
          <aside aria-label="Calendar filters" className="lg:col-span-1 space-y-6">
            {/* Filter Events */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow duration-200">
              <h2 className="text-xl font-bold text-[#123f1e] mb-6 tracking-tight">Filter Events</h2>
              <fieldset>
                <legend className="sr-only">Filter events by category</legend>
                <div className="space-y-3">
                {[
                  ['holiday','Holidays','bg-red-700','text-red-700'],
                  ['academic','Academic Events','bg-amber-700','text-amber-700'],
                  ['earlyDismissal','Early Dismissal','bg-orange-700','text-orange-700'],
                  ['testing','Testing','bg-purple-700','text-purple-700'],
                  ['pta','PTA Events','bg-yellow-700','text-yellow-700'],
                  ['afterSchool','After-School Programs','bg-green-700','text-green-700'],
                ].map(([key,label,bg,color]) => (
                  <div key={key} className="flex items-center">
                    <input
                      id={`filter-${key}`}
                      type="checkbox"
                      checked={selectedFilters[key]}
                      onChange={() => toggleFilter(key)}
                      className={`h-4 w-4 ${color} rounded border-gray-300 focus:ring-[#febc04] focus:ring-offset-2`}
                    />
                    <label htmlFor={`filter-${key}`} className="ml-2 flex items-center text-gray-700 text-sm">
                      <span className={`h-3 w-3 ${bg} rounded-full mr-2`}></span>
                      {label}
                    </label>
                  </div>
                ))}
                </div>
              </fieldset>
            </div>

            {/* Language Selector */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow duration-200">
              <h2 className="text-xl font-bold text-[#123f1e] mb-4 tracking-tight">Language</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setLanguage('en')}
                  aria-pressed={language === 'en'}
                  className={`flex-1 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 ${language === 'en' ? 'bg-[#123f1e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  aria-pressed={language === 'es'}
                  className={`flex-1 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 ${language === 'es' ? 'bg-[#123f1e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ES
                </button>
              </div>
            </div>

            {/* View Options */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow duration-200">
              <h2 className="text-xl font-bold text-[#123f1e] mb-4 tracking-tight">View Options</h2>
              <div className="flex flex-col gap-2 mb-4">
                <button
                  onClick={() => setCurrentView('month')}
                  aria-pressed={currentView === 'month'}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 ${currentView === 'month' ? 'bg-[#123f1e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Month
                </button>
                <button
                  onClick={() => { setCurrentView('week'); setCurrentWeekOffset(0); }}
                  aria-pressed={currentView === 'week'}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 ${currentView === 'week' ? 'bg-[#123f1e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Week
                </button>
                <button
                  onClick={() => setCurrentView('list')}
                  aria-pressed={currentView === 'list'}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 ${currentView === 'list' ? 'bg-[#123f1e] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  List
                </button>
              </div>
              <button
                onClick={() => { setCurrentMonth(new Date().getMonth()); setCurrentYear(new Date().getFullYear()); setCurrentWeekOffset(0); }}
                className="w-full py-3 border-2 border-[#123f1e] text-[#123f1e] rounded-lg hover:bg-[#123f1e] hover:text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#123f1e] focus:ring-offset-2 font-semibold"
              >
                Today
              </button>
            </div>

            {/* Upcoming Events */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow duration-200">
              <h2 className="text-xl font-bold text-[#123f1e] mb-6 tracking-tight">Upcoming Events</h2>
              <div className="space-y-4">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map((event) => (
                    <button
                      type="button"
                      key={event.id}
                      className="border-l-4 pl-4 py-2 cursor-pointer hover:bg-gray-50 rounded-r transition-colors w-full text-left"
                      style={{
                        borderColor: event.category === 'holiday' ? '#b91c1c' : event.category === 'academic' ? '#b45309' : event.category === 'earlyDismissal' ? '#c2410c' : event.category === 'testing' ? '#7e22ce' : event.category === 'pta' ? '#a16207' : '#15803d'
                      }}
                      onClick={() => setSelectedEvent(event)}
                    >
                      <div className="text-xs text-gray-600">{formatDate(event.date)}</div>
                      <div className="font-semibold text-[#123f1e] text-sm">{getEventTitle(event)}</div>
                      <div className="text-xs text-gray-700">{event.allDay ? 'All Day' : `${event.startTime || ''}${event.startTime && event.endTime ? ' - ' : ''}${event.endTime || ''}`}</div>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-500 italic text-sm">No upcoming events based on your filters.</p>
                )}
              </div>
            </div>
          </aside>

          {/* Calendar */}
          <div className="lg:col-span-3" aria-live="polite">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow duration-200">
              <div className="bg-[#123f1e] text-white px-6 py-5 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  {currentView === 'week' ? (
                    (() => {
                      const { days } = getWeekDays();
                      const startLabel = `${days[0].toLocaleString('default', { month: 'short' })} ${days[0].getDate()}`;
                      const endLabel = `${days[6].toLocaleString('default', { month: 'short' })} ${days[6].getDate()}, ${days[6].getFullYear()}`;
                      return <h3 className="text-lg md:text-xl font-bold tracking-tight">Week of {startLabel} – {endLabel}</h3>;
                    })()
                  ) : (
                    <h3 className="text-lg md:text-xl font-bold tracking-tight">{monthNames[currentMonth]} {currentYear}</h3>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={prevMonth}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 focus:ring-offset-[#123f1e]"
                    aria-label={currentView === 'week' ? 'Previous week' : 'Previous month'}
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={nextMonth}
                    className="p-2 rounded-lg hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 focus:ring-offset-[#123f1e]"
                    aria-label={currentView === 'week' ? 'Next week' : 'Next month'}
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200">
                {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day, i) => (
                  <div key={i} className="py-3 font-semibold text-[#123f1e] text-xs md:text-sm">{day}</div>
                ))}
              </div>

              {currentView === 'month' && (
                <div className="grid grid-cols-7 auto-rows-fr border-b border-gray-200 min-h-[400px] md:min-h-[600px]">
                  {days.map((day, index) => {
                    const dayEvents = day ? getEventsForDay(day) : [];
                    const isToday = day && day.toDateString() === new Date().toDateString();
                    return (
                      <div
                        key={index}
                        className={`border-t border-l p-2 h-24 md:h-32 ${day ? 'bg-white' : 'bg-gray-50'} ${isToday ? 'bg-[#febc04]/5 ring-2 ring-inset ring-[#febc04]' : ''} ${index % 7 === 6 ? 'border-r' : ''}`}
                      >
                        {day && (
                          <>
                            <div className={`text-right mb-1 text-sm ${isToday ? 'font-bold text-[#7d5800]' : 'text-gray-700'}`}>{day.getDate()}</div>
                            <div className="space-y-1 overflow-y-auto max-h-[60px] md:max-h-[80px]">
                              {dayEvents.map((event) => (
                                <button
                                  key={event.id}
                                  onClick={() => setSelectedEvent(event)}
                                  className={`text-[10px] md:text-xs w-full text-left px-2 py-1 rounded text-white ${categoryColors[event.category]} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#febc04] truncate`}
                                  title={getEventTitle(event)}
                                >
                                  {getEventTitle(event)}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {currentView === 'week' && (() => {
                const { days: weekDays } = getWeekDays();
                return (
                  <div className="border-b border-gray-200 min-h-[400px]">
                    <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200">
                      {weekDays.map((d, i) => (
                        <div key={i} className="py-3 font-semibold text-[#123f1e]">
                          <div className="text-sm">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]}</div>
                          <div className="text-xs text-gray-600">{d.toLocaleString('default', { month: 'short' })} {d.getDate()}</div>
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 auto-rows-fr min-h-[320px]">
                      {weekDays.map((day, index) => {
                        const dayEvents = getEventsForDay(day);
                        const isToday = day.toDateString() === new Date().toDateString();
                        return (
                          <div
                            key={index}
                            className={`border-t border-l p-3 ${index % 7 === 6 ? 'border-r' : ''} ${isToday ? 'bg-[#febc04]/5 ring-2 ring-inset ring-[#febc04]' : 'bg-white'}`}
                          >
                            <div className={`text-right mb-2 text-sm ${isToday ? 'font-bold text-[#7d5800]' : ''}`}>{day.getDate()}</div>
                            <div className="space-y-2 overflow-y-auto max-h-[260px]">
                              {dayEvents.length === 0 && (<div className="text-xs text-gray-400 italic">{language === 'es' ? 'Sin eventos' : 'No events'}</div>)}
                              {dayEvents.map((event) => (
                                <button
                                  key={event.id}
                                  onClick={() => setSelectedEvent(event)}
                                  className={`text-xs w-full text-left px-2 py-1 rounded text-white ${categoryColors[event.category]} hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#febc04] truncate`}
                                  title={getEventTitle(event)}
                                >
                                  {getEventTitle(event)}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {currentView === 'list' && (
                <div className="border-b border-gray-200 min-h-[600px] p-6">
                  <div className="space-y-4">
                    {getFilteredEvents()
                      .filter(event => event.date.getMonth() === currentMonth && event.date.getFullYear() === currentYear)
                      .sort((a, b) => a.date - b.date)
                      .map(event => (
                        <button
                          type="button"
                          key={event.id}
                          className="flex border-l-4 p-6 bg-gray-50 rounded-r hover:bg-gray-100 transition-colors cursor-pointer w-full text-left"
                          style={{
                            borderColor: event.category === 'holiday' ? '#b91c1c' : event.category === 'academic' ? '#b45309' : event.category === 'earlyDismissal' ? '#c2410c' : event.category === 'testing' ? '#7e22ce' : event.category === 'pta' ? '#a16207' : '#15803d'
                          }}
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div className="mr-6 text-center flex-shrink-0">
                            <div className="font-bold text-2xl text-[#123f1e]">{event.date.getDate()}</div>
                            <div className="text-xs text-gray-600 uppercase">{event.date.toLocaleString('default', { month: 'short' })}</div>
                          </div>
                          <div>
                            <div className="font-semibold text-[#123f1e] text-lg mb-1">{getEventTitle(event)}</div>
                            <div className="text-sm text-gray-700 mb-2">{event.allDay ? 'All Day' : `${event.startTime || ''}${event.startTime && event.endTime ? ' - ' : ''}${event.endTime || ''}`}</div>
                            <div className="text-sm text-gray-600 leading-relaxed">{getEventDescription(event)}</div>
                            <div className="mt-3">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                event.category === 'holiday' ? 'bg-red-100 text-red-800' : event.category === 'academic' ? 'bg-amber-100 text-amber-900' : event.category === 'earlyDismissal' ? 'bg-orange-100 text-orange-900' : event.category === 'testing' ? 'bg-purple-100 text-purple-900' : event.category === 'pta' ? 'bg-yellow-100 text-yellow-900' : 'bg-green-100 text-green-900'
                              }`}>
                                {language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Event Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="event-modal-title">
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full">
            <div className={`${categoryColors[selectedEvent.category]} py-8 px-8 rounded-t-2xl`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 id="event-modal-title" className="text-3xl font-bold text-white tracking-tight">{getEventTitle(selectedEvent)}</h3>
                  <p className="text-white/90 mt-2 text-lg">{formatDate(selectedEvent.date)}</p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="px-8 py-6">
              <div className="text-gray-600 space-y-4">
                <p className="text-lg leading-relaxed">{getEventDescription(selectedEvent)}</p>

                {(selectedEvent.startTime || selectedEvent.endTime || selectedEvent.allDay) && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <Clock className="w-5 h-5 text-[#123f1e]" />
                    <span className="font-medium">{selectedEvent.allDay ? (language === 'es' ? 'Todo el día' : 'All Day') : `${selectedEvent.startTime || ''}${selectedEvent.startTime && selectedEvent.endTime ? ' - ' : ''}${selectedEvent.endTime || ''}`}</span>
                  </div>
                )}

                {selectedEvent.location && (
                  <div className="flex items-center gap-3 text-gray-700">
                    <MapPin className="w-5 h-5 text-[#123f1e]" />
                    <span className="font-medium">{selectedEvent.location}</span>
                  </div>
                )}

                <div className="pt-4">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                    selectedEvent.category === 'holiday' ? 'bg-red-100 text-red-800' : selectedEvent.category === 'academic' ? 'bg-amber-100 text-amber-900' : selectedEvent.category === 'earlyDismissal' ? 'bg-orange-100 text-orange-900' : selectedEvent.category === 'testing' ? 'bg-purple-100 text-purple-900' : selectedEvent.category === 'pta' ? 'bg-yellow-100 text-yellow-900' : 'bg-green-100 text-green-900'
                  }`}>
                    {language === 'es' ? getCategoryNameES(selectedEvent.category) : getCategoryName(selectedEvent.category)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-8 py-5 rounded-b-2xl flex justify-end gap-3">
              <button
                onClick={closeModal}
                className="px-6 py-3 bg-white border-2 border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#123f1e] focus:ring-offset-2 transition-colors"
              >
                Close
              </button>
              {selectedEvent.link && (
                <a
                  href={selectedEvent.link}
                  className="px-6 py-3 bg-[#123f1e] text-white rounded-lg text-sm font-semibold hover:bg-[#1a5a2d] focus:outline-none focus:ring-2 focus:ring-[#123f1e] focus:ring-offset-2 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  More Details
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default Calendar;
